import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FilterState } from '../../FilterDrawer/types';
import { STATUS_FILTER_LABELS } from '../../FilterDrawer/constants';
import { formatDateRangeLabel, TIMEZONE_MAP } from 'utils/common-util';
import { exportJobsList, fetchFilterDetails, fetchJobsList, fetchJobScheduleByHour, fetchJobStatusByType, fetchSummary, getRunDistribution, getRunTrend, getRunTrendAvg, fetchScheduleKpi, fetchGroupChildren, fetchExportCount, fetchCustomStatusCountKpi } from 'service/service';
import { TransformedRunDistributionData } from '../RunDistribution';
import {
  OvertimeCard,
  TransformedRunOvertimeData,
  transformRunOvertimeTrendData,
  transformRunTrendAvgToCards,
  transformJobTypeByStatusData,
  transformScheduleByHourData,
  TransformedChartTableData,
  getScheduledForNextOptions,
  normalizeStatus,
  CUSTOM_STATUS_TILE_SET,
  CUSTOM_STATUS_OPTIONS,
  getInitialPipelineFilters as getInitialFilters,
  buildPageFilters,
  normalizeRangeType,
  isScheduledTile,
  isMiscellaneousSectorSelected,
} from '../utils/pipelineChartUtils';
import moment from 'moment-timezone';
import { useDashboardFilterPersistence } from 'hooks/useDashboardFilterPersistence';
import {filterCache }from '../utils/filterCache';
const DASHBOARD_CONFIG = {
  dashboardId: 'pipeline-run-status',
  dashboardName: 'Pipeline Run Status',
  pageName: 'Pipeline Run Status Dashboard'
};


const DEFAULT_FILTER_OPTIONS = {
  jobType: [],
  jobStatus: [],
  program: [],
  sector: [],
  domain: [],
  gdo: [],
  program_job_name: [],
  scheduleCategory: [],
  scheduledForNext: [],
  sourceCategory: [],
  sourceType: [],
  sourceSubcategory:[],
  orchestratorScheduler: [],
  appName: [],
  businessUnit: [],
  businessStream: [],
  department: [],
  capability: [],
  layer: [],
  market: [],
  technology: [],
  dataAssetName: [],
  bronzeTableName: [],
  silverTableName: [],
  goldTableName: [],
  sustainOwner: [],
  sustainProdDate: [],
  resourceName: [],
  scheduleInfo: [],
  sourceTables: [],
  pecNonPec: [],
  mecNonMec: [],
};

const SCHEDULE_TODAY_WINDOW_HOURS = 20;

const getScheduleTodayRange = () => {
  const cstTimezone = TIMEZONE_MAP.CST || 'America/Chicago';
  const now = moment.tz(cstTimezone);
  return {
    startTime: now.clone().subtract(SCHEDULE_TODAY_WINDOW_HOURS, 'hours').format('DD-MM-YYYY HH:mm:ss'),
    endTime: now.clone().add(SCHEDULE_TODAY_WINDOW_HOURS, 'hours').format('DD-MM-YYYY HH:mm:ss'),
  };
};

const SUPPORTED_SCHEDULE_RANGE_TYPES = new Set([
  'today',
    '1h',
    '30m',
    '15m',
    '10m',
    '5m',
    '7d',
    '30d',
    '90d',
]);

const getStatusTableTotal = (statusCounts: Record<string, unknown>, activeTile: string) => {
  if (String(activeTile).toLowerCase() === 'all') {
    return Object.entries(statusCounts).reduce(
      (sum, [status, count]) => sum + (status === 'Total' ? 0 : Number(count) || 0),
      0,
    );
  }

  const selectedStatus = Object.keys(statusCounts).find(
    status => normalizeStatus(status) === normalizeStatus(activeTile),
  );
  return selectedStatus === undefined ? null : Number(statusCounts[selectedStatus]) || 0;
};

const getColumnFiltersForRequest = (columnFilters: any) => {
  if (!Array.isArray(columnFilters)) return columnFilters;

  return columnFilters.map((filter) => {
    if (!filter?.operator || !['duration_ms', 'avg_duration_ms'].includes(filter.id)) {
      return filter;
    }

    const toMilliseconds = (value: any) => {
      if (value === null || value === undefined || value === '') return value;
      const minutes = Number(value);
      return Number.isNaN(minutes) ? value : Math.round(minutes * 60 * 1000);
    };

    return {
      ...filter,
      values: Array.isArray(filter.values)
        ? filter.values.map(toMilliseconds)
        : toMilliseconds(filter.values),
    };
  });
};

export const usePipelineRunStatus = () => {
  const savedColumnFilterState = filterCache.getColumnFilterState<any>();
  const [isInitialFilterLoading, setIsInitialFilterLoading] = useState(true);
  const [activeTile, setActiveTile] = useState('All');
  const [activeBottomTab, setActiveBottomTab] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [simpleSearchText, setSimpleSearchText] = useState('');
  const [sortingInfo, setSortingInfoState] = useState(() =>
    savedColumnFilterState?.sortModel?.[0]
      ? {
          sortBy: savedColumnFilterState.sortModel[0].columnKey,
          sortDir: savedColumnFilterState.sortModel[0].direction,
        }
      : { sortBy: '', sortDir: '' }
  );

  const setSortingInfo = useCallback((next: { sortBy: string; sortDir: string }) => {
    setSortingInfoState(next);
  }, []);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(() => {
  const savedFilters = filterCache.getAppliedFilters();
  if (savedFilters) {
    const hasSelections = Object.values(savedFilters).some(
      value => Array.isArray(value) && value?.length > 0
    );

    if (hasSelections) {
      return savedFilters;
    }
  }
  return getInitialFilters();
});
  const [filterOptions, setFilterOptions] = useState<any>(DEFAULT_FILTER_OPTIONS);
  const filterOptionsSignatureRef = useRef<Record<string, string>>({});
  const [loadingFilterFields, setLoadingFilterFields] = useState<Record<string, boolean>>({});
  const [summary, setSummary] = useState<any>(null);
  const [scheduledSummary, setScheduledSummary] = useState<any>(null);
  const [customStatusSummary, setCustomStatusSummary] = useState<any>(null);
  const [tableData, setTableData] = useState<any>([]);
  const [columnDetails, setColumnDetails] = useState<Record<string, string>>({});
  const [totalElements, setTotalElements] = useState(0);
  const [isTotalCountLoading, setIsTotalCountLoading] = useState(true);
  const [searchAfter, setSearchAfter] = useState<any>(null); 
  const statusTotalRef = useRef<{ key: string; total: number } | null>(null);
  const latestTableStatusKeyRef = useRef<string | null>(null);
  const summaryRequestIdRef = useRef(0);
  const [runDistribution, setRunDistribution] = useState<TransformedRunDistributionData | null>(null);
  const [runOvertimeTrend, setRunOvertimeTrend] = useState<TransformedRunOvertimeData | null>(null);
  const [runOvertimeCards, setRunOvertimeCards] = useState<OvertimeCard[]>([]); 
  const [jobTypeByStatusData, setJobTypeByStatusData] = useState<TransformedChartTableData | null>(null);
  const [scheduleByHourData, setScheduleByHourData] = useState<TransformedChartTableData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isKpiLoading, setIsKpiLoading] = useState(true);
  const [isScheduledKpiLoading, setIsScheduledKpiLoading] = useState(true);
  const [isCustomStatusKpiLoading, setIsCustomStatusKpiLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [dependentInfoLoading, setDependentInfoLoading] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('pipelineRunStatusAutoRefresh');
    return saved ? Number(saved) : 5;
  });
  const lastFilterStateRef = useRef<Record<string, string>>({});
  const isLoadingRef = useRef<Record<string, boolean>>({});
  const initialLoadDoneRef = useRef(false);
  const [startDate, setStartDate] = useState<string | null>(() => sessionStorage.getItem('pipelineRunStatusStartDate'));
  const [endDate, setEndDate] = useState<string | null>(() => sessionStorage.getItem('pipelineRunStatusEndDate'));
  const [savedRangeType, setSavedRangeType] = useState<string | null>(() => sessionStorage.getItem('pipelineRunStatusDateRangeType'));
  const [savedTimeZone, setSavedTimeZone] = useState<string | null>(() => sessionStorage.getItem('pipelineRunStatusDateRangeTimeZone'));
  const [isRunDistributionLoading, setIsRunDistributionLoading] = useState(false);
  const [isRunTrendLoading, setIsRunTrendLoading] = useState(false);
  const [isRunTrendCardsLoading, setIsRunTrendCardsLoading] = useState(false);
  const [isJobTypeStatusLoading, setIsJobTypeStatusLoading] = useState(false);
  const [isScheduleByHourLoading, setIsScheduleByHourLoading] = useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] = useState<string[]>([]);
  const latestStateRef = useRef<any>({});
  const jobsListRequestIdRef = useRef(0);
  const latestColumnFiltersRef = useRef<Record<string, any>>(
    savedColumnFilterState?.payload ?? {}
  );
  const lastSortingInfoRef = useRef({ sortBy: '', sortDir: '' });
  const lastPaginationInfoRef = useRef({ page: 1, pageSize: 10 });
  const [isFiltersLoaded, setIsFiltersLoaded] = useState(false);
  const latestFiltersRef = useRef<FilterState>(appliedFilters);
  const [groupBy, setGroupBy] = useState<string>('none');
  const [isGrouped, setIsGrouped] = useState(false);
  const [isScheduleInitApplied, setIsScheduleInitApplied] = useState(false);

const [isLoadingMoreChildren, setIsLoadingMoreChildren] = useState(false);
const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);
const loadMoreChildren = useCallback(async (groupByValue: string, groupValue: string, currentCount: number, parentRow: any) => {
  setIsLoadingMoreChildren(true);
  setLoadingGroupId(`${groupByValue}-${groupValue}`);
  
  try {
    const state = latestStateRef.current;
    const includeScheduledForNext = String(state.activeTile).toLowerCase() === 'scheduled';
    const { globalFilters, dateRange } = applyFilters(includeScheduledForNext, undefined, includeScheduledForNext);
    
    const isInProgress = normalizeStatus(state.activeTile) === 'inprogress';
    const params = {
      groupBy: groupByValue,
      groupValue: groupValue,
      afterCount: currentCount,
      pageSize: 50,
      pageFilters: {
        jobStatus: [state.activeTile],
        ...(state.searchText && { searchText: state.searchText }),
      },
      sortingInfo: state.sortingInfo.sortBy ? state.sortingInfo : { sortBy: '', sortDir: '' },
      globalFilters,
      dateRange,
      ...(isInProgress && { indexOverride: "inProgressIndex" })
    };
    
    const response = await fetchGroupChildren(params);
    
    if (response?.success && response?.data) {
      return {
        children: response.data.children,
        hasMore: response.data.hasMore,
        nextOffset: response.data.nextOffset
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error loading more children:', error);
    return null;
  } finally {
    setIsLoadingMoreChildren(false);
    setLoadingGroupId(null);
  }
}, [appliedFilters, savedRangeType, startDate, endDate, activeTile, searchText, sortingInfo]);

const preScheduleRangeRef = useRef<{
  savedRangeType: string | null;
  savedTimeZone: string | null;
  startDate: string | null;
  endDate: string | null;
} | null>(null);

const applyRangeToState = (range: {
  savedRangeType: string | null;
  savedTimeZone: string | null;
  startDate: string | null;
  endDate: string | null;
}) => {

  latestStateRef.current = {
    ...latestStateRef.current,
    savedRangeType: range.savedRangeType,
    startDate: range.startDate,
    endDate: range.endDate,
  };

  if (range.savedRangeType == null) {
    sessionStorage.removeItem('pipelineRunStatusDateRangeType');
  } else {
    sessionStorage.setItem('pipelineRunStatusDateRangeType', range.savedRangeType);
  }
  if (range.savedTimeZone == null) {
    sessionStorage.removeItem('pipelineRunStatusDateRangeTimeZone');
  } else {
    sessionStorage.setItem('pipelineRunStatusDateRangeTimeZone', range.savedTimeZone);
  }
  if (range.startDate == null) {
    sessionStorage.removeItem('pipelineRunStatusStartDate');
  } else {
    sessionStorage.setItem('pipelineRunStatusStartDate', range.startDate);
  }
  if (range.endDate == null) {
    sessionStorage.removeItem('pipelineRunStatusEndDate');
  } else {
    sessionStorage.setItem('pipelineRunStatusEndDate', range.endDate);
  }

  setSavedRangeType(range.savedRangeType);
  setSavedTimeZone(range.savedTimeZone);
  setStartDate(range.startDate);
  setEndDate(range.endDate);
  window.dispatchEvent(new Event('pipelineStatusDateRangeChanged'));

};

  const handleSavedFiltersLoaded = useCallback((savedFilters: FilterState) => {
    const defaultFilters = getInitialFilters();
    const sessionFilters = filterCache.getAppliedFilters();
    const hasSessionFilters = sessionFilters && Object.keys(sessionFilters).some(
      key => {
        const sessionValue = sessionFilters[key as keyof FilterState];
        const defaultValue = defaultFilters[key as keyof FilterState];
        if (Array.isArray(sessionValue) && Array.isArray(defaultValue)) {
          if (sessionValue?.length === 0 && defaultValue?.length === 0) {
            return false;
          }
          const sessionStr = JSON.stringify(sessionValue);
          const defaultStr = JSON.stringify(defaultValue);
          return sessionStr !== defaultStr;
        }
        return sessionValue !== defaultValue;
      }
    );
    if (!hasSessionFilters && savedFilters && Object.keys(savedFilters)?.length > 0) {
      setAppliedFilters(savedFilters);
      filterCache.saveAppliedFilters(savedFilters);
      if (savedFilters.jobStatus && savedFilters.jobStatus?.length === 1) {
        setActiveTile(savedFilters.jobStatus[0].value);
      } else if (savedFilters.jobStatus && savedFilters.jobStatus?.length > 1) {
        setActiveTile('All');
      }
    } else {
      console.log('[handleSavedFiltersLoaded] Session filters exist, skipping DB restore');
    }

    setIsFiltersLoaded(true);
  }, []);

  const {
    isLoading: isFiltersLoading,
    isSaving: isSavingFilters,
    hasSavedFilters,
    savedFilters: savedDefaultFilters,
    manualSave: manualSaveFilters,
    resetToSaved: resetToSavedFilters,
  } = useDashboardFilterPersistence({
    config: DASHBOARD_CONFIG,
    currentFilters: appliedFilters,
    onFiltersLoaded: handleSavedFiltersLoaded,
    debounceDelay: 1000,
    enabled: true
  });


const scheduleInitRef = useRef(false);
useEffect(() => {
  if (scheduleInitRef.current) return;
  if (!isFiltersLoaded) return;
  if (!isScheduledTile(activeTile)) {
    scheduleInitRef.current = true;    
    setIsScheduleInitApplied(true);
    return;
  }
  const isUntouchedDefault = !savedRangeType || savedRangeType === 'today';
  if (!isUntouchedDefault) {
    scheduleInitRef.current = true;
    setIsScheduleInitApplied(true);
    return;
  }

  scheduleInitRef.current = true;
  preScheduleRangeRef.current = {
    savedRangeType,
    savedTimeZone,
    startDate,
    endDate,
  };

  const range = getScheduleTodayRange();
  const isoStart = moment.tz(range.startTime, 'DD-MM-YYYY HH:mm:ss', TIMEZONE_MAP.CST).toISOString();
  const isoEnd = moment.tz(range.endTime, 'DD-MM-YYYY HH:mm:ss', TIMEZONE_MAP.CST).toISOString();

  applyRangeToState({
    savedRangeType: 'custom',
    savedTimeZone: 'CST',
    startDate: isoStart,
    endDate: isoEnd,
  });
  setPage(1);
  setSearchAfter(null);
}, [isFiltersLoaded, activeTile, savedRangeType, startDate, endDate]);
  useEffect(() => {
    latestStateRef.current = {
      page,
      pageSize,
      searchText,
      activeTile,
      sortingInfo,
      appliedFilters,
      savedRangeType,
      startDate,
      endDate,
      activeBottomTab,
      groupBy,
      searchAfter
    };
  }, [
    page,
    pageSize,
    searchText,
    activeTile,
    sortingInfo,
    appliedFilters,
    savedRangeType,
    startDate,
    endDate,
    activeBottomTab,
    groupBy,
    searchAfter
  ]);

  useEffect(() => {
    latestFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const manualSaveFiltersWithLatest = useCallback(async (filtersToSave?: FilterState) => {
    const filters = filtersToSave || latestFiltersRef.current;
    return manualSaveFilters(filters);
  }, [manualSaveFilters]);

  let dateRangeType = '';
  if (savedRangeType) {
    const isCustomRange = savedRangeType === 'custom' && startDate && endDate;
    if (isCustomRange) {
      dateRangeType = 'selected date range';
    } else {
      dateRangeType = formatDateRangeLabel(savedRangeType);
    }
  }
  const timeZone = savedTimeZone ?? 'CST';
  const ianaTimezone = TIMEZONE_MAP[timeZone];

 const isScheduledForNextEnabled =
  SUPPORTED_SCHEDULE_RANGE_TYPES.has(normalizeRangeType(savedRangeType)) ||
  savedRangeType === 'custom';

  const loadSummary = async () => {
    const requestId = ++summaryRequestIdRef.current;
    const requestedTile = latestStateRef.current.activeTile;
    const state = latestStateRef.current;
    const hasColumnLevelFilters = Object.keys(latestColumnFiltersRef.current).length > 0;
    const shouldLoadStatusTotal =
      requestedTile !== 'UniqueJobs' &&
      !isScheduledTile(requestedTile) &&
      (!state.groupBy || state.groupBy === 'none') &&
      !hasColumnLevelFilters;
    try {
      const { globalFilters, dateRange } = applyFilters(false);
      let params = {
        pageFilters: {
          ...(searchText && { searchText }),
          ...(state.searchText && { searchText: state.searchText }),
        },
        globalFilters,
        dateRange
      };
      const statusTotalKey = JSON.stringify({
        globalFilters,
        dateRange,
        searchText: params.pageFilters.searchText || '',
        activeTile: requestedTile,
      });
      const cachedStatusTotal =
        shouldLoadStatusTotal && statusTotalRef.current?.key === statusTotalKey
          ? statusTotalRef.current.total
          : null;
      setIsTotalCountLoading(shouldLoadStatusTotal && cachedStatusTotal === null);
      if (cachedStatusTotal !== null && latestTableStatusKeyRef.current === statusTotalKey) {
        setTotalElements(cachedStatusTotal);
      }

      const result = await fetchSummary(params);
      if (requestId !== summaryRequestIdRef.current) return;
      setSummary(result.data);
      const statusCounts = result?.data?.data?.result;
      if (statusCounts && typeof statusCounts === 'object') {
        const total = getStatusTableTotal(statusCounts, requestedTile);
        if (total !== null && !hasColumnLevelFilters) {
          statusTotalRef.current = { key: statusTotalKey, total };

          if (
            latestTableStatusKeyRef.current === statusTotalKey &&
            shouldLoadStatusTotal &&
            Object.keys(latestColumnFiltersRef.current).length === 0
          ) {
            setTotalElements(total);
          }
        }
      }
    } catch (error) {
      console.error('Error loading summary kpi:', error);
    } finally {
      if (
        requestId === summaryRequestIdRef.current &&
        Object.keys(latestColumnFiltersRef.current).length === 0
      ) {
        setIsTotalCountLoading(false);
      }
    }
  };

  const loadScheduleKpiData = async () => {
  try {
    const state = latestStateRef.current;
    const hasRange =
      state.savedRangeType === 'custom'
        ? Boolean(state.startDate && state.endDate)
        : Boolean(state.savedRangeType);

    if (!hasRange) {
      setScheduledSummary({ data: { Scheduled: 0 } });
      return;
    }

    const { globalFilters, dateRange } = applyFilters(true, undefined, true);
    const params = {
      pageFilters: { ...(state.searchText && { searchText: state.searchText }) },
      globalFilters,
      dateRange,
    };
    const result = await fetchScheduleKpi(params);
    setScheduledSummary(result.data);
  } catch (error) {
    console.error('Error loading schedule kpi:', error);
  }
};

const loadCustomStatusKpiData = async () => {
  try {
    const state = latestStateRef.current;

    const { globalFilters, dateRange } = applyFilters(false);
    const params = {
      pageFilters: { ...(state.searchText && { searchText: state.searchText }) },
      globalFilters,
      dateRange,
    };
    const result = await fetchCustomStatusCountKpi(params);
    setCustomStatusSummary(result.data);
  } catch (error) {
    console.error('Error loading custom status kpi:', error);
  }
}

  const dateTimeRangeParam = (state: any, isScheduledCall = false) => {
    if (state.savedRangeType === "custom" && state.startDate && state.endDate) {
      return {
        startTime: moment(state.startDate).tz(ianaTimezone).format("DD-MM-YYYY HH:mm:ss"),
        endTime: moment(state.endDate).tz(ianaTimezone).format("DD-MM-YYYY HH:mm:ss"),
      };
    }
    if (state.savedRangeType === "today") {
      if (isScheduledCall) {
        return getScheduleTodayRange();
      }
      return {
        startTime: moment.tz(ianaTimezone).startOf("day").format("DD-MM-YYYY HH:mm:ss"),
        endTime: moment.tz(ianaTimezone).endOf("day").format("DD-MM-YYYY HH:mm:ss"),
      };
    }

    return {};
  };

  const getFilterValues = (items: any[] = []) => {
    const values = items.map((item: any) => item.value);

    if (values.some((v: string) => String(v).toLowerCase() === 'all')) {
      return ['all'];
    }
    return values;
  };

    const getFilterValuesSourceSubcategory = (items: any[] = []) => {
    const values = items.map((item: any) => String(item.value));
    // If 'all' is present, remove it and return the other values (case-insensitive)
    const filtered = values.filter(v => String(v).toLowerCase() !== 'all');
    // Deduplicate and return
    return Array.from(new Set(filtered));
  };
  const getScheduledForNextRange = (selectedHours: number) => {
    const cstTimezone = TIMEZONE_MAP.CST || 'America/Chicago';
    const now = moment.tz(cstTimezone);
    const endOfDay = now.clone().endOf('day');
    const selectedEnd = now.clone().add(selectedHours, 'hours');
    const endTime = selectedEnd.isAfter(endOfDay) ? endOfDay : selectedEnd;

    return {
      timeZone: 'CST',
      startTime: now.format('DD-MM-YYYY HH:mm:ss'),
      endTime: endTime.format('DD-MM-YYYY HH:mm:ss'),
      selectedOption: selectedHours
    };
  };

  const getScheduledForNextFilter = (params: any, state: any) => {
    const selectedValue = state.appliedFilters.scheduledForNext[0]?.value;
    if (selectedValue === 'rest_of_day') {
      const cstTimezone = TIMEZONE_MAP.CST || 'America/Chicago';
      const now = moment.tz(cstTimezone);
      const endOfDay = now.clone().endOf('day');
      params.globalFilters.scheduledForNext = {
        timeZone: 'CST',
        startTime: now.format('DD-MM-YYYY HH:mm:ss'),
        endTime: endOfDay.format('DD-MM-YYYY HH:mm:ss'),
        selectedOption: 0
      };
    } else {
      const selectedHours = Number(selectedValue);
      if (!Number.isNaN(selectedHours) && selectedHours > 0) {
        params.globalFilters.scheduledForNext = getScheduledForNextRange(selectedHours);
      }
    }
  };

    const resolveCustomStatusTileJobStatus = (
    activeTile: string,
    columnFilters: any
  ): { isCustomStatusTile: boolean; jobStatusList: string[] | null; cleanedColumnFilters: any } => {
    const list = Array.isArray(columnFilters) ? columnFilters : [];

    // Only custom tiles inject the implicit In Progress seed. Non-custom tiles
    // must NOT strip any user-selected `execution_status` filter.
    if (!CUSTOM_STATUS_TILE_SET.has(activeTile)) {
      return {
        isCustomStatusTile: false,
        jobStatusList: null,
        cleanedColumnFilters: list,
      };
    }

    const executionStatusColumnFilter = list.find(
      (f: any) => f?.id === 'execution_status'
    );

    const columnStatusValues = (
      executionStatusColumnFilter?.selOptions?.length
        ? executionStatusColumnFilter.selOptions
        : executionStatusColumnFilter?.values || []
    ).filter(Boolean);

    const hasNonInProgressColumnStatus = columnStatusValues.some(
      (s: any) =>
        !['all', 'inprogress', 'in progress'].includes(String(s).toLowerCase())
    );

    const onlyInProgress =
      columnStatusValues.length > 0 &&
      columnStatusValues.every((s: any) =>
        ['all', 'inprogress', 'in progress'].includes(String(s).toLowerCase())
      );

    const cleanedColumnFilters = onlyInProgress
      ? list.filter((f: any) => f?.id !== 'execution_status')
      : list;

    if (hasNonInProgressColumnStatus) {
      return {
        isCustomStatusTile: true,
        jobStatusList: columnStatusValues,
        cleanedColumnFilters,
      };
    }

    return {
      isCustomStatusTile: true,
      jobStatusList: ['InProgress'],
      cleanedColumnFilters,
    };
  };



const applyFilters = (
  includeScheduledForNext: boolean = false,
  filtersOverride?: FilterState,
  isScheduledCall: boolean = false,
) => {    const state = latestStateRef.current;
    // When called for a specific dropdown's options (filtersOverride), use the
    // in-progress drawer selections instead of the last committed appliedFilters,
    const effectiveAppliedFilters = filtersOverride ?? state.appliedFilters;
    const params: any = {
      globalFilters: {},
      dateRange: {},
    };
    if (effectiveAppliedFilters.jobType?.length > 0) params.globalFilters.jobType = getFilterValues(effectiveAppliedFilters.jobType);
    if (effectiveAppliedFilters.jobStatus?.length > 0) params.globalFilters.jobStatus = getFilterValues(effectiveAppliedFilters.jobStatus);
    if (effectiveAppliedFilters.program?.length > 0) params.globalFilters.program = getFilterValues(effectiveAppliedFilters.program);
    if (effectiveAppliedFilters?.program_job_name?.length > 0) params.globalFilters.program_job_name = getFilterValues(effectiveAppliedFilters.program_job_name);
    if (effectiveAppliedFilters?.productShortName?.length > 0) params.globalFilters.productShortName = getFilterValues(effectiveAppliedFilters.productShortName);
    const selectedSectors = effectiveAppliedFilters.sector || [];
    const hasAllSectorSelected = selectedSectors.some((s: any) => {
      const val = typeof s === 'string' ? s : s.value;
      return String(val).toLowerCase() === 'all';
    });

    if (hasAllSectorSelected) {
      params.globalFilters.sector = ['all'];
    } else {
      let combinedSectors = selectedSectors.filter((s: any) => {
        const val = typeof s === 'string' ? s : s.value;
        return String(val).toLowerCase() !== 'miscellaneous';
      });
      const hasMiscSelected = selectedSectors.some((s: any) => {
        const val = typeof s === 'string' ? s : s.value;
        return String(val).toLowerCase() === 'miscellaneous';
      });
      if (hasMiscSelected) {
        const miscSubs = effectiveAppliedFilters.miscellaneousSectors || [];
        if (miscSubs.length > 0) {
          // Check if 'all' is selected in miscellaneous sectors
          const hasAllInMisc = miscSubs.some((s: any) => {
            const val = typeof s === 'string' ? s : s.value;
            return String(val).toLowerCase() === 'all';
          });
          const selectedMiscSectors = miscSubs.filter((s: any) => {
            const val = typeof s === 'string' ? s : s.value;
            return String(val).toLowerCase() !== 'all';
          });

        if (hasAllInMisc) {
          // Expand 'all' to all actual miscellaneous sectors from filterOptions
          // If no detailed data is available, do not inject a literal 'Miscellaneous' value.
          const allMiscSectors = (filterOptions.miscellaneousSectors || []).filter((s: any) => {
            const val = typeof s === 'string' ? s : s.value;
            return String(val).toLowerCase() !== 'all';
          });
          
          if (allMiscSectors?.length > 0) {
            combinedSectors = combinedSectors.concat(allMiscSectors);
          } else if (selectedMiscSectors?.length > 0) {
            combinedSectors = combinedSectors.concat(selectedMiscSectors);
          }
        } else {
          // Send only the specific miscellaneous sectors selected (excluding 'All')
          combinedSectors = combinedSectors.concat(selectedMiscSectors);
        }
      } 
    }
    if (combinedSectors?.length > 0) {
      // Extract sector values handling both string and object formats
      const sectorValues = combinedSectors.map((s: any) => {
        return typeof s === 'string' ? s : s.value;
      });
      params.globalFilters.sector = sectorValues;
    }
  }
    if (effectiveAppliedFilters.domain && effectiveAppliedFilters.domain?.length > 0) params.globalFilters.domain = getFilterValues(effectiveAppliedFilters.domain);
    if (effectiveAppliedFilters.gdo && effectiveAppliedFilters.gdo?.length > 0) params.globalFilters.gdo = getFilterValues(effectiveAppliedFilters.gdo);
    if (effectiveAppliedFilters.scheduleCategory?.length > 0) params.globalFilters.scheduleCategory = getFilterValues(effectiveAppliedFilters.scheduleCategory);
    if ( includeScheduledForNext &&
      isScheduledForNextEnabled &&
      effectiveAppliedFilters?.scheduledForNext?.length > 0
    ) {
      getScheduledForNextFilter(params, state);
    }
    if (effectiveAppliedFilters?.sourceCategory?.length > 0) params.globalFilters.sourceCategory = getFilterValues(effectiveAppliedFilters.sourceCategory);
    if (effectiveAppliedFilters?.sourceType?.length > 0) params.globalFilters.sourceType = getFilterValues(effectiveAppliedFilters.sourceType);
    if (effectiveAppliedFilters?.sourceSubcategory?.length > 0) params.globalFilters.sourceSubcategory = getFilterValuesSourceSubcategory(effectiveAppliedFilters.sourceSubcategory);
    if (savedRangeType) {
      params.dateRange = {
        dateTimeRangeType: state.savedRangeType,
        timeZone: savedTimeZone,
        ...dateTimeRangeParam(state, isScheduledCall),
      };
    }
    if (effectiveAppliedFilters?.orchestratorScheduler && effectiveAppliedFilters?.orchestratorScheduler?.length > 0) params.globalFilters.orchestratorScheduler = getFilterValues(effectiveAppliedFilters.orchestratorScheduler);
    if (effectiveAppliedFilters?.appName && effectiveAppliedFilters?.appName?.length > 0) params.globalFilters.appName = getFilterValues(effectiveAppliedFilters.appName);
    if (effectiveAppliedFilters?.businessUnit && effectiveAppliedFilters?.businessUnit?.length > 0) params.globalFilters.businessUnit = getFilterValues(effectiveAppliedFilters.businessUnit);
    if (effectiveAppliedFilters?.businessStream && effectiveAppliedFilters?.businessStream?.length > 0) params.globalFilters.businessStream = getFilterValues(effectiveAppliedFilters.businessStream);
    if (effectiveAppliedFilters?.department && effectiveAppliedFilters?.department?.length > 0) params.globalFilters.department = getFilterValues(effectiveAppliedFilters.department);
    if (effectiveAppliedFilters?.capability && effectiveAppliedFilters?.capability?.length > 0) params.globalFilters.capability = getFilterValues(effectiveAppliedFilters.capability);
    if (effectiveAppliedFilters?.layer && effectiveAppliedFilters?.layer?.length > 0) params.globalFilters.layer = getFilterValues(effectiveAppliedFilters.layer);
    if (effectiveAppliedFilters?.market && effectiveAppliedFilters?.market?.length > 0) params.globalFilters.market = getFilterValues(effectiveAppliedFilters.market);
    if (effectiveAppliedFilters?.technology && effectiveAppliedFilters?.technology?.length > 0) params.globalFilters.technology = getFilterValues(effectiveAppliedFilters.technology);
    if (effectiveAppliedFilters?.dataAssetName && effectiveAppliedFilters?.dataAssetName?.length > 0) params.globalFilters.dataAssetName = getFilterValues(effectiveAppliedFilters.dataAssetName);
    if (effectiveAppliedFilters?.bronzeTableName && effectiveAppliedFilters?.bronzeTableName?.length > 0) params.globalFilters.bronzeTableName = getFilterValues(effectiveAppliedFilters.bronzeTableName);
    if (effectiveAppliedFilters?.silverTableName && effectiveAppliedFilters?.silverTableName?.length > 0) params.globalFilters.silverTableName = getFilterValues(effectiveAppliedFilters.silverTableName);
    if (effectiveAppliedFilters?.goldTableName && effectiveAppliedFilters?.goldTableName?.length > 0) params.globalFilters.goldTableName = getFilterValues(effectiveAppliedFilters.goldTableName);
    if (effectiveAppliedFilters?.sustainOwner && effectiveAppliedFilters?.sustainOwner?.length > 0) params.globalFilters.sustainOwner = getFilterValues(effectiveAppliedFilters.sustainOwner);
    if (effectiveAppliedFilters?.sustainProdDate && effectiveAppliedFilters?.sustainProdDate?.length > 0) params.globalFilters.sustainProdDate = getFilterValues(effectiveAppliedFilters.sustainProdDate);
    if (effectiveAppliedFilters?.resourceName && effectiveAppliedFilters?.resourceName?.length > 0) params.globalFilters.resourceName = getFilterValues(effectiveAppliedFilters.resourceName);
    if (effectiveAppliedFilters?.resourceGroup && effectiveAppliedFilters?.resourceGroup?.length > 0) params.globalFilters.resourceGroup = getFilterValues(effectiveAppliedFilters.resourceGroup);
    if (effectiveAppliedFilters?.subscriptionName && effectiveAppliedFilters?.subscriptionName?.length > 0) params.globalFilters.subscriptionName = getFilterValues(effectiveAppliedFilters.subscriptionName);
    if (effectiveAppliedFilters?.scheduleInfo && effectiveAppliedFilters?.scheduleInfo?.length > 0)
      params.globalFilters.scheduleInfo = getFilterValues(effectiveAppliedFilters.scheduleInfo);
    if (effectiveAppliedFilters?.sourceTables && effectiveAppliedFilters?.sourceTables?.length > 0)
      params.globalFilters.sourceTables = getFilterValues(effectiveAppliedFilters.sourceTables);
    if (effectiveAppliedFilters?.pecNonPec && effectiveAppliedFilters?.pecNonPec?.length > 0)
      params.globalFilters.pecNonPec = getFilterValues(effectiveAppliedFilters.pecNonPec);
    if (effectiveAppliedFilters?.mecNonMec && effectiveAppliedFilters?.mecNonMec?.length > 0)
      params.globalFilters.mecNonMec = getFilterValues(effectiveAppliedFilters.mecNonMec);
    if (effectiveAppliedFilters?.additionalFilters?.length > 0) {
      const additionalFilterMap: Record<string, any[]> = {};

      effectiveAppliedFilters.additionalFilters.forEach((filter: any) => {
        if (!filter?.field || !filter?.value) return;

        if (!additionalFilterMap[filter.field]) {
          additionalFilterMap[filter.field] = [];
        }

        additionalFilterMap[filter.field].push(filter.value);
      });

      Object.keys(additionalFilterMap).forEach((key) => {
        params.globalFilters[key] = additionalFilterMap[key];
      });
    }
    return params;
  }

  const loadJobsList = async (
    columnFilters?: Record<string, any>,
    sortingOverride?: { sortBy: string; sortDir: string }
  ) => {

    if (columnFilters !== undefined) {
      latestColumnFiltersRef.current = columnFilters;
    } else {
      const cached = filterCache.getColumnFilterState<any>();
      latestColumnFiltersRef.current = Array.isArray(cached?.payload)
        ? cached.payload
        : [];
    }
    let requestId = jobsListRequestIdRef.current;
    let waitingForStatusTotal = false;
    try {
      const state = latestStateRef.current;
      const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
        resolveCustomStatusTileJobStatus(state.activeTile, latestColumnFiltersRef.current);
      const includeScheduledForNext = String(state.activeTile).toLowerCase() === 'scheduled';
      const { globalFilters, dateRange } = applyFilters(includeScheduledForNext, undefined, includeScheduledForNext);
      const currentSearchAfter = state.searchAfter;

      // When 'UniqueJobs' tile is active, treat it as 'All' for the data query
      const isUniqueJobsTile = state.activeTile === 'UniqueJobs';
      const effectiveJobStatus = isUniqueJobsTile
        ? 'All'
        : state.activeTile;
      let searchNext = null;

      if (state.groupBy && state.groupBy !== 'none' && state.page > 1) {
        const afterKey = currentSearchAfter;
        if (afterKey && typeof afterKey === 'object' && Object.keys(afterKey).length > 0) {
          searchNext = afterKey;
        }
      }
      else if (state.page > 1 && currentSearchAfter) {

        if (typeof currentSearchAfter === 'object' && Object.keys(currentSearchAfter).length > 0) {
          searchNext = currentSearchAfter;
        }
      }


      const isScheduled = isScheduledTile(state.activeTile);

      const userSortBy = state.sortingInfo.sortBy;
      const userSortIsValidForTile = Boolean(userSortBy) && (
        isScheduled
          ? true
          : userSortBy !== 'expected_start_time' && userSortBy !== 'last_run_start_time' && userSortBy !== 'schedule_missed'
      );

      const sortingPayload = sortingOverride ?? (
        userSortIsValidForTile
          ? state.sortingInfo
          : isScheduled
            ? { sortBy: 'expected_start_time', sortDir: 'asc' }
            : { sortBy: '', sortDir: '' }
      );
      const hasColumnLevelFilters = Object.keys(latestColumnFiltersRef.current).length > 0;

      let effectiveColumnFilters = latestColumnFiltersRef.current;
      let customStatusJobStatus: string[] | null = null;

      if (isCustomStatusTile) {
        const executionStatusColumnFilter = Array.isArray(latestColumnFiltersRef.current)
          ? latestColumnFiltersRef.current.find((f) => f?.id === 'execution_status')
          : null;

        const columnStatusValues = (
          executionStatusColumnFilter?.selOptions?.length
            ? executionStatusColumnFilter.selOptions
            : executionStatusColumnFilter?.values || []
        ).filter(Boolean);

        const drawerStatusValues = (globalFilters.jobStatus || []).map(String);

        const isNonInProgress = (s: any) =>
          !['all', 'inprogress', 'in progress'].includes(String(s).toLowerCase());

        // Column filter takes precedence; fall back to the drawer status filter.
        const explicitStatuses =
          columnStatusValues.some(isNonInProgress)
            ? columnStatusValues
            : drawerStatusValues.some(isNonInProgress)
              ? drawerStatusValues
              : [];

        if (explicitStatuses.length > 0) {
          customStatusJobStatus = explicitStatuses;
          // Strip redundant execution_status column filter to avoid double-filtering
          effectiveColumnFilters = (latestColumnFiltersRef.current || [])
            .filter((f: any) => f?.id !== 'execution_status');
        } else {
          customStatusJobStatus = ['InProgress'];
        }
      }

      // ─── Effective status list for index routing ────────────────────────
      const effectiveJobStatusList: string[] = isCustomStatusTile
        ? (jobStatusList || ['InProgress'])
        : [effectiveJobStatus];

      const isInProgress =
        effectiveJobStatusList.length === 1 &&
        normalizeStatus(effectiveJobStatusList[0]) === 'inprogress';

      // Fetch Completed jobs on click on Short Run Count Tile/Widget

      const isShortRunClicked = effectiveJobStatusList?.length === 1 && effectiveJobStatusList[0] === STATUS_FILTER_LABELS.SHORT_RUN;

      // ─── Params ─────────────────────────────────────────────────────────
      const params = {
        pageFilters: {
          jobStatus: isShortRunClicked ? [STATUS_FILTER_LABELS.SUCCESSFUL, STATUS_FILTER_LABELS.FAILED] : effectiveJobStatusList,
          ...(state.searchText && { searchText: state.searchText }),
          ...(isShortRunClicked && { customStatus: STATUS_FILTER_LABELS.SHORT_RUN })
        },
        paginationInfo: {
          page: state.page,
          pageSize: state.pageSize,
          ...(searchNext && { searchNext }),
        },
        sortingInfo: sortingPayload,
        ...(isCustomStatusTile && {
          globalFilters: { ...globalFilters, customStatus: state.activeTile },
        }),
        dateRange,
        ...(Array.isArray(cleanedColumnFilters) && cleanedColumnFilters.length > 0 && {
          columnFilters: getColumnFiltersForRequest(cleanedColumnFilters),
        }),
        ...(state.groupBy && state.groupBy !== 'none' && { groupBy: state.groupBy }),
        ...(isInProgress && { indexOverride: 'inProgressIndex' }),
        ...(!isCustomStatusTile && {
          globalFilters: { ...globalFilters },
        }),
        uniqueJobsMode: isUniqueJobsTile,
      };

      const statusCountKey = JSON.stringify({
        globalFilters,
        dateRange,
        searchText: state.searchText || '',
        activeTile: state.activeTile,
      });
      latestTableStatusKeyRef.current = statusCountKey;
      const shouldUseStatusCount =
        !isUniqueJobsTile &&
        !isCustomStatusTile &&                          // ← crucial
        !isScheduledTile(state.activeTile) &&
        (!state.groupBy || state.groupBy === 'none') &&
        !hasColumnLevelFilters;
      const hasCachedStatusTotal = statusTotalRef.current?.key === statusCountKey;
      waitingForStatusTotal = shouldUseStatusCount && !hasCachedStatusTotal;
      if (hasColumnLevelFilters || waitingForStatusTotal) {
        setIsTotalCountLoading(true);
      } else {
        setIsTotalCountLoading(false);
      }

      requestId = ++jobsListRequestIdRef.current;
      setIsLoading(true);

      const response = await fetchJobsList(params);

      if (requestId !== jobsListRequestIdRef.current) return;

      setColumnDetails(response?.data?.columnDetails ?? {});

      if (response?.data?.isGrouped === true) {
        const groupedData = response.data.data?.groups || [];
        setTableData(groupedData);
        const statusCount = shouldUseStatusCount && statusTotalRef.current?.key === statusCountKey
          ? statusTotalRef.current.total
          : null;
        setTotalElements(statusCount ?? response.data.totalExecutions ?? 0);
        if (!waitingForStatusTotal) setIsTotalCountLoading(false);

        const afterKeyFromResponse = response.data.searchNext;
        setSearchAfter(afterKeyFromResponse);

        setIsGrouped(true);
      } else {
        response.data.data = response.data.data.map((datum: any, index: number) => ({
          ...datum,
          id: index + 1,
          isLineageExpanded: false,
        }));
        const flatData = response?.data?.data || [];
        setTableData(flatData);
        // For 'UniqueJobs' tile, show the unique job count (by job.id) as totalElements
        if (isUniqueJobsTile) {
          setTotalElements(response?.data?.uniqueJobCountByJobId || response?.data?.totalExecutions || 0);
        } else {
          const statusCount = shouldUseStatusCount && statusTotalRef.current?.key === statusCountKey
            ? statusTotalRef.current.total
            : null;
          setTotalElements(statusCount ?? response?.data?.totalExecutions ?? 0);
        }
        if (!waitingForStatusTotal) setIsTotalCountLoading(false);
        const searchNextFromResponse = response?.data?.searchNext;
        setSearchAfter(searchNextFromResponse || null);

        setIsGrouped(false);
        lastSortingInfoRef.current = sortingInfo;
        lastPaginationInfoRef.current = { page: state.page, pageSize: state.pageSize };
      }
      setTimeout(() => {
        if (requestId === jobsListRequestIdRef.current) {
          setIsLoading(false);
        }
      }, 0);

    } catch (error) {
      if (requestId !== jobsListRequestIdRef.current) return;
      console.error('Error loading jobs list:', error);
      setIsLoading(false);
      if (!waitingForStatusTotal) setIsTotalCountLoading(false);
    }
  };


  useEffect(() => {
  if (page === 1) {
    setSearchAfter(null);
  }
  }, [page]);

  useEffect(() => {
  setPage(1);
  setSearchAfter(null);
  }, [sortingInfo]);

  useEffect(() => {
    setPage(1);
    setSearchAfter(null);
  }, [groupBy]);




  const loadRunDistribution = useCallback(async () => {
    try {
      setIsRunDistributionLoading(true);
      const state = latestStateRef.current;
      const { globalFilters, dateRange } = applyFilters();
      const params: any = {
        pageFilters: { ...(state.searchText && { searchText: state.searchText }) },
        globalFilters,
        dateRange,
      };

      const response = await getRunDistribution(params);
      if (!response?.data?.data) {
        setRunDistribution({ result: [] });
        return;
      }

      const transformedData: TransformedRunDistributionData = {
        result: Array.isArray(response.data.data) ? response.data.data : [],
      };
      setRunDistribution(transformedData);
    } catch (error) {
      console.error('Error loading run distribution:', error);
      setRunDistribution({ result: [] });
    } finally {
      setIsRunDistributionLoading(false);
    }
  }, [appliedFilters, savedRangeType, startDate, endDate, savedTimeZone]); 

  const loadRunOvertimeTrend = useCallback(async () => {
    try {
      setIsRunTrendLoading(true);
      const state = latestStateRef.current;
      const { globalFilters, dateRange } = applyFilters();
      const params: any = {
        pageFilters: { ...(state.searchText && { searchText: state.searchText }) },
        globalFilters,
        dateRange,
      };

      const response = await getRunTrend(params);
      if (response?.data?.data) {
        const transformed = transformRunOvertimeTrendData(response.data.data);
        setRunOvertimeTrend(transformed);
      } else {
        setRunOvertimeTrend(null);
      }
    } catch (error) {
      console.error('Error loading run overtime trend:', error);
      setRunOvertimeTrend(null);
    } finally {
      setIsRunTrendLoading(false);
    }
  }, [appliedFilters, savedRangeType, startDate, endDate, savedTimeZone]); 

  const loadRunOvertimeCards = useCallback(async () => {
    try {
      setIsRunTrendCardsLoading(true);
      const state = latestStateRef.current;
      const { globalFilters, dateRange } = applyFilters();
      const params: any = {
        pageFilters: { ...(state.searchText && { searchText: state.searchText }) },
        globalFilters,
        dateRange,
      };

      const response = await getRunTrendAvg(params);
      if (response?.data?.data) {
        const cards = transformRunTrendAvgToCards(response.data.data);
        setRunOvertimeCards(cards);
      } else {
        setRunOvertimeCards([]);
      }
    } catch (error) {
      console.error('Error loading run overtime cards:', error);
      setRunOvertimeCards([]);
    } finally {
      setIsRunTrendCardsLoading(false);
    }
  }, [appliedFilters, savedRangeType, startDate, endDate, savedTimeZone]); 

  const loadJobTypeByStatus = useCallback(async () => {
    try {
      setIsJobTypeStatusLoading(true);
      setJobTypeByStatusData(null);
      const state = latestStateRef.current;
      const { globalFilters, dateRange } = applyFilters();
      const params: any = { globalFilters, dateRange, pageFilters: { ...(state.searchText && { searchText: state.searchText }) } };

      const response = await fetchJobStatusByType(params);
      if (response?.data?.data) {
        const transformed = transformJobTypeByStatusData(response.data.data);
        setJobTypeByStatusData(transformed);
      } else {
        setJobTypeByStatusData(null);
      }
    } catch (error) {
      console.error('Error loading job type by status:', error);
      setJobTypeByStatusData(null);
    } finally {
      setIsJobTypeStatusLoading(false);
    }
  }, [appliedFilters, savedRangeType, startDate, endDate, savedTimeZone]);

  const loadScheduleByHour = useCallback(async () => {
    try {
      setIsScheduleByHourLoading(true);
      const state = latestStateRef.current;
      const { globalFilters, dateRange } = applyFilters();
      const params: any = { globalFilters, dateRange, pageFilters: { ...(state.searchText && { searchText: state.searchText }) } };


      const response = await fetchJobScheduleByHour(params);
      if (response?.data?.data) {
        const transformed = transformScheduleByHourData(response.data.data);
        setScheduleByHourData(transformed);
      } else {
        setScheduleByHourData(null);
      }
    } catch (error) {
      console.error('Error loading schedule by hour:', error);
      setScheduleByHourData(null);
    } finally {
      setIsScheduleByHourLoading(false);
    }
  }, [appliedFilters, savedRangeType, startDate, endDate, savedTimeZone]);

  useEffect(() => {
    if (activeBottomTab === undefined) return;
    switch (activeBottomTab) {
      case 0:
        loadRunDistribution();
        break;
      case 1:
        loadRunOvertimeTrend();
        loadRunOvertimeCards();
        break;
      case 2:
        loadJobTypeByStatus();
        break;
      case 3:
        loadScheduleByHour();
        break;
      default:
        break;
    }
  }, [activeBottomTab, searchText, appliedFilters, savedRangeType, startDate, endDate]);

  useEffect(() => {
    if (appliedFilters) {
      const defaultFilters = getInitialFilters();
      const hasMeaningfulSelections = Object.keys(appliedFilters).some(
        key => {
          const currentValue = appliedFilters[key as keyof FilterState];
          const defaultValue = defaultFilters[key as keyof FilterState];

          if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
            const currentStr = JSON.stringify(currentValue);
            const defaultStr = JSON.stringify(defaultValue);
            return currentStr !== defaultStr;
          }

          return currentValue !== defaultValue;
        }
      );

      if (hasMeaningfulSelections) {
        filterCache.saveAppliedFilters(appliedFilters);
      } else {
        console.log('[Save] Skipping save - filters are at default values');
      }
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (!isFiltersLoaded) return;
    const fieldsToLoad = Object.keys(appliedFilters).filter(key => {
      const value = appliedFilters[key as keyof FilterState];
      return Array.isArray(value) && value.length > 0;
    });

    fieldsToLoad.forEach(field => {
      loadAllDropDowns(
        true,
        appliedFilters,
        field as keyof FilterState
      );
    });
  }, [isFiltersLoaded]);

  const isValidCachedData = (cachedData: any[], field: string, currentFilters: FilterState): boolean => {
    if (!cachedData) return false;
    if (field === 'program') {
      return Array.isArray(cachedData);
    }

    if (field === 'gdo') {
      const hasProgram = currentFilters?.program && currentFilters?.program?.length > 0;
      return hasProgram && Array.isArray(cachedData);
    }

    if (field === 'sourceSubcategory') {
      const hasSourceType = currentFilters?.sourceType && currentFilters?.sourceType?.length > 0;
      return hasSourceType && Array.isArray(cachedData);
    }

    if (field === 'orchestratorScheduler') {
      const hasSourceType = currentFilters?.sourceType && currentFilters?.sourceType?.length > 0;
      return hasSourceType && Array.isArray(cachedData);
    }

    const dependencyMap: Record<string, string[]> = {
      'dataAssetName': ['program'],
      'bronzeTableName': ['program', 'layer'],
      'silverTableName': ['program', 'layer'],
      'goldTableName': ['program', 'layer'],
      'businessStream': ['program'],
      'domain': ['program'],
      'sourceSubcategory': ['sourceType'],
      'orchestratorScheduler': ['sourceType'],
      'scheduleInfo': ['program', 'layer'],
      'sourceTables': ['program', 'layer'],
      'pecNonPec': ['program', 'layer'],
      'mecNonMec': ['program', 'layer'],
    };

    const dependencies = dependencyMap[field] || [];
    for (const dep of dependencies) {
      const depValue = currentFilters[dep as keyof FilterState];
      if (!depValue || (Array.isArray(depValue) && depValue?.length === 0)) {
        return false;
      }
    }

    return Array.isArray(cachedData);
  };

  const loadAllDropDowns = useCallback(async (
    force: boolean = false,
    filtersOverride?: FilterState,
    field?: keyof FilterState
  ) => {
    if (field && isLoadingRef.current[field]) {
      return;
    }
    const currentFilters = filtersOverride || appliedFilters;
    if (isMiscellaneousSectorSelected(currentFilters.sector || [])) {
      return;
    }
    const pageFilters = buildPageFilters(latestStateRef.current);
    const pageKey = JSON.stringify(pageFilters);
    const globalStateKey = `${filterCache.generateStateKey(currentFilters, field ? [field] : [])}|${pageKey}`;
    const getRelevantFields = (fieldName: string): string[] => {
      const relevanceMap: Record<string, string[]> = {
        'program': [],
        'gdo': ['program', 'sector', 'market', 'jobType', 'businessUnit', 'appName'],
        'domain': ['program', 'sector', 'market'],
        'businessStream': ['program'],
        'dataAssetName': ['program', 'sector', 'market'],
        'bronzeTableName': ['program', 'layer', 'sector'],
        'silverTableName': ['program', 'layer', 'sector'],
        'goldTableName': ['program', 'layer', 'sector'],
        'sourceSubcategory': ['sourceType'],
        'orchestratorScheduler': ['sourceType'],
        'sector': ['program'],
        'market': ['program', 'sector'],
        'jobType': ['program', 'sector', 'market'],
        'businessUnit': ['program', 'sector', 'market', 'jobType'],
        'appName': ['program', 'sector', 'market', 'jobType', 'businessUnit'],
        'scheduleCategory': [],
        'scheduledForNext': [],
        'sourceCategory': [],
        'sourceType': [],
        'department': [],
        'capability': [],
        'technology': [],
        'subscriptionName': [],
        'resourceGroup': [],
        'resourceName': [],
        'sustainOwner': [],
        'sustainProdDate': [],
        'layer': ['program'],
        'miscellaneousSectors': ['sector'],
      };

      return relevanceMap[fieldName] || [];
    };

    const relevantFields = field ? getRelevantFields(field) : [];
    const fieldStateKey = `${filterCache.generateStateKeyWithRelevantFields(currentFilters, relevantFields)}|${pageKey}`;
    const hasFilters = Object.values(currentFilters).some(
      value => Array.isArray(value) && value.length > 0
    );


    if (!force && field) {
      const previousGlobalStateKey = lastFilterStateRef.current[`_global_${field}`];
      const globalStateChanged = previousGlobalStateKey && globalStateKey !== previousGlobalStateKey;
      if (!globalStateChanged) {
        const cachedData = filterCache.get<any[]>(field, fieldStateKey);
        if (cachedData !== null && isValidCachedData(cachedData, field, currentFilters)) {
          setFilterOptions((prev: any) => ({ ...prev, [field]: cachedData }));
          setLoadingFilterFields((prev: any) => ({ ...prev, [field]: false }));
          lastFilterStateRef.current[field] = fieldStateKey;
          lastFilterStateRef.current[`_global_${field}`] = globalStateKey;
          return;
        } else {
          console.log(`[Cache] Cache miss or invalid for ${field}`);
        }
      } else {
        console.log(`[Cache] Global filter state changed for ${field}, fetching fresh data`);
      }
    }
    if (field) {
      isLoadingRef.current[field] = true;
      setLoadingFilterFields((prev) => ({ ...prev, [field]: true }));
      console.log(`[Cache] Making API call for ${field}`);
    }

    try {
      const activeTileIsScheduled = isScheduledTile(latestStateRef.current?.activeTile);     
      const { globalFilters, dateRange } = applyFilters(false, filtersOverride, activeTileIsScheduled);
      const signature = JSON.stringify({ globalFilters, pageFilters });
     
      const cacheKey = field || '__all__';
      if (!force && filterOptionsSignatureRef.current[cacheKey] === signature && field) {
        const existingData = filterOptions[field];
        if (existingData && Array.isArray(existingData)) {
          console.log(`[Cache] Using existing data for ${field}`);
          filterCache.set(field, fieldStateKey, existingData || []);
          lastFilterStateRef.current[field] = fieldStateKey;
          lastFilterStateRef.current[`_global_${field}`] = globalStateKey;
        }
        if (field) {
          isLoadingRef.current[field] = false;
          setLoadingFilterFields((prev) => ({ ...prev, [field]: false }));
        }
        return;
      }

      let result;
      if (pageFilters) {
        result = await fetchFilterDetails({
          pageFilters,
          globalFilters,
          dateRange,
          field,
        });
      } else {
        result = await fetchFilterDetails({ globalFilters, dateRange, field });
      }
      const apiData = result?.data?.data || {};

      setFilterOptions((prev: any) => ({ ...prev, ...apiData }));

      if (field && apiData[field] !== undefined) {
        const optionsData = apiData[field];
        filterCache.set(field, fieldStateKey, optionsData || []);
        lastFilterStateRef.current[field] = fieldStateKey;
        lastFilterStateRef.current[`_global_${field}`] = globalStateKey;
        console.log(`[Cache] Cached data for ${field}, count: ${optionsData?.length || 0}`);
      }

      if (field) {
        filterOptionsSignatureRef.current[field] = signature;
      } else {
        Object.keys(apiData).forEach((key) => {
          filterOptionsSignatureRef.current[key] = signature;
          if (apiData[key] !== undefined) {
            const fieldRelevantFields = getRelevantFields(key);
            const fieldStateKeyLocal = `${filterCache.generateStateKeyWithRelevantFields(currentFilters, fieldRelevantFields)}|${pageKey}`;
            const globalKeyLocal = `${filterCache.generateStateKey(currentFilters, [key])}|${pageKey}`;
            filterCache.set(key, fieldStateKeyLocal, apiData[key] || []);
            lastFilterStateRef.current[key] = fieldStateKeyLocal;
            lastFilterStateRef.current[`_global_${key}`] = globalKeyLocal;
          }
        });
        filterOptionsSignatureRef.current.__all__ = signature;
      }
    } catch (error) {
      console.error('Error loading filter options:', error);

      if (field) {
        const staleCache = filterCache.get<any[]>(field, fieldStateKey);
        if (staleCache !== null) {
          setFilterOptions((prev: any) => ({ ...prev, [field]: staleCache }));
        }
      }
    } finally {
      if (field) {
        isLoadingRef.current[field] = false;
        setLoadingFilterFields((prev) => ({ ...prev, [field]: false }));
      }
    }
  }, [appliedFilters, applyFilters, filterOptions]);
  const getColumnFilterValues = useCallback(async (field: string): Promise<any[]> => {
    const state = latestStateRef.current;
    if (field === 'custom_status' && state.activeTile !== STATUS_FILTER_LABELS.SHORT_RUN) {
      return [...CUSTOM_STATUS_OPTIONS];
    }

    // Column Filter: Show only Short Run dropdown value
    if (field === 'custom_status' && state.activeTile === STATUS_FILTER_LABELS.SHORT_RUN) {
      return [STATUS_FILTER_LABELS.SHORT_RUN];
    }
    // ── Strip implicit InProgress seed for custom status tiles ──
    const { cleanedColumnFilters } = resolveCustomStatusTileJobStatus(
      state.activeTile,
      latestColumnFiltersRef.current
    );

    const pageFilters = {
      jobStatus: [state.activeTile], // Note: backend treats custom status tiles by mapping this later, but for advance filter options it needs the raw activeTile
      ...(state.searchText && { searchText: state.searchText }),
    }
    const activeTileIsScheduled = isScheduledTile(latestStateRef.current?.activeTile);
    const { globalFilters, dateRange } = applyFilters(false, undefined, activeTileIsScheduled);

    const result = await fetchFilterDetails({
      globalFilters,
      pageFilters,
      ...(Object.keys(cleanedColumnFilters || {}).length > 0 && {
        columnFilters: getColumnFiltersForRequest(cleanedColumnFilters),
      }),
      dateRange,
      field,
      isColumnFilter: true,
    });

    const apiData = result?.data?.data || {};
    const values = apiData[field];
    if (field === 'sector' && Array.isArray(apiData.miscellaneousSectors) && apiData.miscellaneousSectors.length > 0) {
      const regularSectors = (Array.isArray(values) ? values : []).filter(
        (value: any) => String(value).toLowerCase() !== 'miscellaneous'
      );
      return [
        ...regularSectors,
        {
          label: 'Miscellaneous',
          value: '__miscellaneous__',
          children: apiData.miscellaneousSectors,
        },
      ];
    }
    return Array.isArray(values) ? values : [];
  }, [applyFilters]);

  useEffect(() => {
    if (!isFiltersLoaded) return;
    
    const loadAllFilters = async () => {
      setIsInitialFilterLoading(true);
      try {
        await loadAllDropDowns(true);
        initialLoadDoneRef.current = true;
      } catch (error) {
        console.error('Error loading initial filters:', error);
      } finally {
        setIsInitialFilterLoading(false);
      }
    };
    
    if (!initialLoadDoneRef.current) {
      loadAllFilters();
    }
  }, [isFiltersLoaded, loadAllDropDowns]);

  const loadOtherRunStatusKpi = async () => {
    setIsKpiLoading(true);
    try {
      await loadSummary();
    } finally {
      setIsKpiLoading(false);
    }
  };

const scheduledKpiRequestIdRef = useRef(0);

const loadScheduledKpi = async () => {
  const requestId = ++scheduledKpiRequestIdRef.current;
  setIsScheduledKpiLoading(true);
  try {
    await loadScheduleKpiData();
  } finally {
    if (requestId === scheduledKpiRequestIdRef.current) {
      setIsScheduledKpiLoading(false);
    }
  }
};

  const loadCustomStatusKpi = async () => {
    setIsCustomStatusKpiLoading(true);
    try {
      // Trigger separate API to fetch computed data of custom_status as Hung/Long Running/On Track
      await loadCustomStatusKpiData();
    } finally {
      setIsCustomStatusKpiLoading(false);
    }
  };

  const shouldLoadScheduledKpi = () => {
    const state = latestStateRef.current;
    const selectedJobStatus = (state.appliedFilters.jobStatus || []).map((item: any) => String(item.value).toLowerCase());
    if (selectedJobStatus.length === 0) return true;
    if (selectedJobStatus.includes('all')) return true;
    return selectedJobStatus.includes('scheduled');
  };

  const loadRunStatusKpi = async () => {
    const state = latestStateRef.current;
    const isScheduledActive = isScheduledTile(state.activeTile);

    if (isScheduledActive) {
      await loadScheduledKpi();
      return;
    }

    if (shouldLoadScheduledKpi()) {
      await Promise.all([loadOtherRunStatusKpi(), loadScheduledKpi(), loadCustomStatusKpi()]);
    } else {
      await loadOtherRunStatusKpi();
      setScheduledSummary(null);
      setIsScheduledKpiLoading(false);
      setIsCustomStatusKpiLoading(false);
    }
  };

  const parseInterval = (value: string | null): number | null => {
    if (value === null || value === '0') return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  };

  useEffect(() => {
    const syncSessionStorage = () => {
      // Auto refresh
      const savedInterval = sessionStorage.getItem('pipelineRunStatusAutoRefresh');
      const parsed = parseInterval(savedInterval);
       setAutoRefreshInterval(prev => (prev === parsed ? prev : parsed));

      // Date range
      const newRangeType = sessionStorage.getItem('pipelineRunStatusDateRangeType');
      const newTimeZone = sessionStorage.getItem('pipelineRunStatusDateRangeTimeZone');
      const newStartDate = sessionStorage.getItem('pipelineRunStatusStartDate');
      const newEndDate = sessionStorage.getItem('pipelineRunStatusEndDate');

      setSavedRangeType(prev => (prev === newRangeType ? prev : newRangeType));
      setSavedTimeZone(prev => (prev === newTimeZone ? prev : newTimeZone));
      setStartDate(prev => (prev === newStartDate ? prev : newStartDate));
      setEndDate(prev => (prev === newEndDate ? prev : newEndDate));
    };

    window.addEventListener('storage', syncSessionStorage);
    window.addEventListener('pipelineStatusSessionStorageUpdated', syncSessionStorage);

    const interval = setInterval(syncSessionStorage, 500);

    return () => {
      window.removeEventListener('storage', syncSessionStorage);
      window.removeEventListener('pipelineStatusSessionStorageUpdated', syncSessionStorage);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handlePipelineSearch = (event: any) => {
      const searchText = event.detail?.searchText || '';
      setSimpleSearchText(searchText);
      setSearchText(searchText);
      setPage(1);
    };

    window.addEventListener('pipelineSearch', handlePipelineSearch);
    return () => window.removeEventListener('pipelineSearch', handlePipelineSearch);
  }, []);

  useEffect(() => {
    if (isFiltersLoaded) {
      loadAllDropDowns(true);
    }
  }, [savedRangeType, startDate, endDate, activeTile, isFiltersLoaded]);


useEffect(() => {
  if (!isFiltersLoaded) return;
  if (!isScheduledForNextEnabled) {
    if (!isScheduledTile(latestStateRef.current?.activeTile)) {
      setScheduledSummary({ data: { Scheduled: 0 } });
    }
    setAppliedFilters(prev => {
      if (!prev.scheduledForNext || prev.scheduledForNext.length === 0) return prev;
      return { ...prev, scheduledForNext: [] };
    });
  }
}, [isScheduledForNextEnabled, isFiltersLoaded, isScheduledTile]);

const appliedFiltersKey = useMemo(() => {
  const stableStringify = (obj: any): string => {
    if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
    if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`;
    const keys = Object.keys(obj).sort();
    return `{${keys
      .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
      .join(',')}}`;
  };
  return stableStringify(appliedFilters);
}, [appliedFilters]);

const querySignature = useMemo(
  () =>
    JSON.stringify({
      activeTile,
      searchText,
      appliedFiltersKey,
      savedRangeType,
      startDate,
      endDate,
      groupBy,
    }),
  [activeTile, searchText, appliedFiltersKey, savedRangeType, startDate, endDate, groupBy]
);

  useEffect(() => {
  if (isFiltersLoaded && isScheduleInitApplied) {
    loadRunStatusKpi();
  }
}, [querySignature, isFiltersLoaded, isScheduleInitApplied]);

  useEffect(() => {
  if (isFiltersLoaded && isScheduleInitApplied) {
    loadJobsList();
  }
}, [querySignature, page, pageSize, sortingInfo, isFiltersLoaded, isScheduleInitApplied]);

  useEffect(() => {
    if (!autoRefreshInterval) return;

    const interval = setInterval(async () => {
      const { activeBottomTab } = latestStateRef.current;
      await Promise.all([
        loadRunStatusKpi(),
        loadJobsList(latestColumnFiltersRef.current),
        loadAllDropDowns(true),
      ]);
      switch (activeBottomTab) {
        case 0:
          await loadRunDistribution();
          break;
        case 1:
          await Promise.all([loadRunOvertimeTrend(), loadRunOvertimeCards()]);
          break;
        case 2:
          await loadJobTypeByStatus();
          break;
        case 3:
          await loadScheduleByHour();
          break;
        default:
          break;
      }
    }, autoRefreshInterval * 60 * 1000);

    return () => clearInterval(interval);
  }, [autoRefreshInterval]);
 
const handleTileClick = (key: string) => {
  const enteringSchedule = isScheduledTile(key);
  const leavingSchedule =
    isScheduledTile(latestStateRef.current?.activeTile) && !enteringSchedule;

  latestStateRef.current = { ...latestStateRef.current, activeTile: key };
  setActiveTile(key);
  setPage(1);
  setSearchAfter(null);

  if (enteringSchedule) {
    preScheduleRangeRef.current = {
      savedRangeType,
      savedTimeZone,
      startDate,
      endDate,
    };

    
    const isUntouchedDefault =
      !savedRangeType || savedRangeType === 'today';

    if (isUntouchedDefault) {
      const range = getScheduleTodayRange();
      const isoStart = moment
        .tz(range.startTime, 'DD-MM-YYYY HH:mm:ss', TIMEZONE_MAP.CST)
        .toISOString();
      const isoEnd = moment
        .tz(range.endTime, 'DD-MM-YYYY HH:mm:ss', TIMEZONE_MAP.CST)
        .toISOString();

      applyRangeToState({
        savedRangeType: 'custom',
        savedTimeZone: 'CST',
        startDate: isoStart,
        endDate: isoEnd,
      });
    }

    loadAllDropDowns(true);
  } else if (leavingSchedule) {

    if (preScheduleRangeRef.current) {
      const pre = preScheduleRangeRef.current;
      const wasOverwritten =
        !pre.savedRangeType || pre.savedRangeType === 'today';
      if (wasOverwritten) {
        applyRangeToState(pre);
      }
      preScheduleRangeRef.current = null;
    }
  }
};

  const handleApplyFilters = useCallback((filters: FilterState, skipCacheClear: boolean = false) => {
    const hadPreviousJobStatus = appliedFilters.jobStatus.length > 0;
    const clearedAllJobStatus = !filters.jobStatus || filters.jobStatus.length === 0;

    setAppliedFilters(filters);
    if (!skipCacheClear) {
      const changedFields = Object.keys(filters).filter(
        key => JSON.stringify(filters[key as keyof FilterState]) !==
          JSON.stringify(appliedFilters[key as keyof FilterState])
      );

      const dependentFieldsMap: Record<string, string[]> = {
        'program': ['gdo', 'domain', 'businessStream', 'dataAssetName', 'bronzeTableName', 'silverTableName', 'goldTableName', 'layer'],
        'sector': ['miscellaneousSectors'],
        'sourceType': ['sourceSubcategory', 'orchestratorScheduler'],
        'layer': ['goldTableName', 'silverTableName', 'bronzeTableName'],
      };

      changedFields.forEach(field => {
        const dependents = dependentFieldsMap[field] || [];
        dependents.forEach(dep => filterCache.clear(dep));
      });
    }

    if (filters.jobStatus && filters?.jobStatus?.length === 1) {
      const selectedStatus = filters.jobStatus[0].value;
      setActiveTile(selectedStatus);
    } else if (filters.jobStatus && filters.jobStatus?.length > 1) {
      setActiveTile('All');
    } else if (clearedAllJobStatus && hadPreviousJobStatus) {
      setActiveTile('All');
    }
    setPage(1);
  }, [appliedFilters.jobStatus?.length, appliedFilters]);

  const getExportRecordCount = async (): Promise<number> => {
    try {
      const state = latestStateRef.current;
      const includeScheduledForNext = String(state.activeTile).toLowerCase() === 'scheduled';
      const { globalFilters, dateRange } = applyFilters(includeScheduledForNext, undefined, includeScheduledForNext);

      const isUniqueJobsTile = state.activeTile === 'UniqueJobs';
      const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
        resolveCustomStatusTileJobStatus(state.activeTile, latestColumnFiltersRef.current);

      const effectiveJobStatuses = isCustomStatusTile
        ? jobStatusList
        : [isUniqueJobsTile ? 'All' : state.activeTile];

      const isInProgress = !isCustomStatusTile &&
        normalizeStatus(effectiveJobStatuses?.[0]) === 'inprogress';

      // Fetch Completed jobs on click on Short Run Count Tile/Widget to export
      const isShortRunClicked = effectiveJobStatuses?.length === 1 && effectiveJobStatuses[0] === STATUS_FILTER_LABELS.SHORT_RUN;

      const params = {
        pageFilters: {
          jobStatus: isShortRunClicked ? [STATUS_FILTER_LABELS.SUCCESSFUL, STATUS_FILTER_LABELS.FAILED] : effectiveJobStatuses,
          ...(state.searchText && { searchText: state.searchText }),
          ...(isShortRunClicked && { customStatus: STATUS_FILTER_LABELS.SHORT_RUN })
        },
        paginationInfo: {
          page: 1,
          pageSize: 1,
        },
        globalFilters: {
          ...globalFilters,
          ...(isCustomStatusTile && { customStatus: state.activeTile }),
        },
        ...(Object.keys(cleanedColumnFilters || {}).length > 0 && {
          columnFilters: getColumnFiltersForRequest(cleanedColumnFilters),
        }),
        dateRange,

        ...(state.groupBy && state.groupBy !== 'none' && { groupBy: state.groupBy }),

        ...(isInProgress && { indexOverride: 'inProgressIndex' }),
        uniqueJobsMode: isUniqueJobsTile,
      };

      const response = await fetchExportCount(params);

      return response?.data?.count || 0;
    } catch (error) {
      console.error('Error fetching export record count:', error);
      return 0;
    }
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 800));
    try {
      const state = latestStateRef.current;
      const includeScheduledForNext = String(state.activeTile).toLowerCase() === 'scheduled';
      const { globalFilters, dateRange } = applyFilters(includeScheduledForNext, undefined, includeScheduledForNext);

      const sortingChanged =
        state.sortingInfo.sortBy !== lastSortingInfoRef.current.sortBy ||
        state.sortingInfo.sortDir !== lastSortingInfoRef.current.sortDir;

      const sortingPayload = {
        ...state.sortingInfo,
        ...(sortingChanged && lastSortingInfoRef.current.sortBy && {
          prevSort: lastSortingInfoRef.current.sortBy,
          prevSortDir: lastSortingInfoRef.current.sortDir,
        }),
      };

      const isUniqueJobsTile = state.activeTile === 'UniqueJobs';
      const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
        resolveCustomStatusTileJobStatus(state.activeTile, latestColumnFiltersRef.current);

      const effectiveJobStatuses = isCustomStatusTile
        ? jobStatusList
        : [isUniqueJobsTile ? 'All' : state.activeTile];

      const isInProgress = !isCustomStatusTile &&
        normalizeStatus(effectiveJobStatuses?.[0]) === 'inprogress';

      // Fetch Completed jobs on click on Short Run Count Tile/Widget to export

      const isShortRunClicked = effectiveJobStatuses?.length === 1 && effectiveJobStatuses[0] === STATUS_FILTER_LABELS.SHORT_RUN;


      let params = {
        pageFilters: {
          jobStatus: isShortRunClicked ? [STATUS_FILTER_LABELS.SUCCESSFUL, STATUS_FILTER_LABELS.FAILED] : effectiveJobStatuses,
          ...(state.searchText && { searchText: state.searchText }),
          ...(isShortRunClicked && { customStatus: STATUS_FILTER_LABELS.SHORT_RUN })
        },
        paginationInfo: {
          page: 1,
          pageSize: 10000,
        },
        sortingInfo: state.sortingInfo.sortBy ? sortingPayload : { sortBy: '', sortDir: '' },
        formatType: 'EXCEL',
        globalFilters: {
          ...globalFilters,
          ...(isCustomStatusTile && { customStatus: state.activeTile }),
        },
        ...(Object.keys(cleanedColumnFilters || {}).length > 0 && {
          columnFilters: getColumnFiltersForRequest(cleanedColumnFilters),
        }),
        dateRange,
        groupBy: state.groupBy || 'none',

        ...(isInProgress && { indexOverride: "inProgressIndex" }),
        uniqueJobsMode: isUniqueJobsTile,
      };

      await Promise.all([exportJobsList(params), minDelay]);
    } catch (error) {
      console.error('Error exporting jobs:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const fetchPdfData = async (): Promise<any[]> => {
    try {
      const state = latestStateRef.current;
      const includeScheduledForNext = String(state.activeTile).toLowerCase() === 'scheduled';
      const { globalFilters, dateRange } = applyFilters(includeScheduledForNext, undefined, includeScheduledForNext);

      const sortingPayload = state.sortingInfo.sortBy ? state.sortingInfo : { sortBy: '', sortDir: '' };

      const isUniqueJobsTile = state.activeTile === 'UniqueJobs';

      const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
        resolveCustomStatusTileJobStatus(state.activeTile, latestColumnFiltersRef.current);

      const effectiveJobStatuses = isCustomStatusTile
        ? jobStatusList
        : [isUniqueJobsTile ? 'All' : state.activeTile];

      const isInProgress = !isCustomStatusTile &&
        normalizeStatus(effectiveJobStatuses?.[0]) === 'inprogress';

      const params = {
        pageFilters: {
          jobStatus: effectiveJobStatuses,
          ...(state.searchText && { searchText: state.searchText }),
        },
        paginationInfo: { page: 1, pageSize: 500 },
        sortingInfo: sortingPayload,
        globalFilters: {
          ...globalFilters,
          ...(isCustomStatusTile && { customStatus: state.activeTile }),
        },
        dateRange,
        ...(Object.keys(cleanedColumnFilters || {}).length > 0 && {
          columnFilters: getColumnFiltersForRequest(cleanedColumnFilters),
        }),
        ...(isInProgress && { indexOverride: 'inProgressIndex' }),
        uniqueJobsMode: isUniqueJobsTile,
      };

      const response = await fetchJobsList(params);
      return response?.data?.data || [];
    } catch (error) {
      console.error('Error fetching PDF data:', error);
      return [];
    }
  };
  
  return {
    activeTile,
    activeBottomTab,
    setActiveBottomTab,
    page,
    pageSize,
    searchText,
    simpleSearchText,
    sortingInfo,
    appliedFilters,
    loadAllDropDowns,
    loadingFilterFields,
    summary,
    tableData,
    totalElements,
    isTotalCountLoading,
    isLoading,
    isExporting,
    isScheduledKpiLoading,
    isCustomStatusKpiLoading,
    scheduledSummary,
    customStatusSummary,
    filterOptions,
    setPage,
    setPageSize,
    setSearchText,
    setSimpleSearchText,
    setSortingInfo,
    setGroupBy,
    groupBy,
    isGrouped,
    setIsGrouped,
    handleTileClick,
    handleApplyFilters,
    getExportRecordCount,
    handleExport,
    loadSummary,
    dateRangeType,
    setIsLoading,
    isKpiLoading,
    runDistribution, 
    runOvertimeTrend,
    runOvertimeCards,
    loadRunDistribution,
    ianaTimezone,
    jobTypeByStatusData,
    scheduleByHourData,
    isRunDistributionLoading,
    isRunTrendLoading,
    isRunTrendCardsLoading,
    isJobTypeStatusLoading,
    isScheduleByHourLoading,
    isFiltersLoaded,
    isInitialFilterLoading,
    isFiltersLoading,
    isSavingFilters,
    hasSavedFilters,
    fetchPdfData,
    savedDefaultFilters,
    manualSaveFilters: manualSaveFiltersWithLatest,
    resetToSavedFilters,
      loadMoreChildren,
  isLoadingMoreChildren,
  loadingGroupId,
  setLoadingGroupId,
  startDate,
  endDate,
  savedRangeType,
  timeZone,
  isScheduledForNextEnabled,
  dateTimeRangeParam,
  dependentInfoLoading,
  setDependentInfoLoading,
  selectedAdditionalFilters,
  setSelectedAdditionalFilters,
  loadRunStatusKpi,
  loadJobsList,
  getColumnFilterValues,
  columnDetails
  };
};
